import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./authApi";

const POLL_MS = 5000; // hoe vaak we /api/access/status pollen in het wachtscherm

/**
 * useAuth — centrale auth/access-state voor App.js.
 *
 * user:         { id, username, name, role } | null   (null = uitgelogd)
 * accessStatus: "none" | "pending" | "approved" | "denied" | null (null = nog niet gecheckt)
 *
 * Coaches krijgen altijd "approved" (server regelt dit al, maar we zetten
 * het ook hier zodat de UI niet eerst een flits "none" laat zien).
 */
export function useAuth() {
  const [user, setUser] = useState(() => api.getSession()?.user || null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [membership, setMembership] = useState(null); // { status, until, cancelAtPeriodEnd, comp, hasBilling, priceLabel }
  const [discord, setDiscord] = useState(null);       // { linked, enabled }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const statusRef = useRef(null);
  useEffect(() => { statusRef.current = accessStatus; }, [accessStatus]);

  const refreshStatus = useCallback(async () => {
    if (!api.getSession()) return;
    try {
      const data = await api.getAccessStatus();
      setAccessStatus(data.status);
      setMembership(data.membership || null);
      setDiscord(data.discord || null);
    } catch {
      // netwerkblip: status laten staan, volgende poll probeert opnieuw
    }
  }, []);

  // Zodra er een user is, direct status checken en daarna blijven pollen
  // zolang we niet "approved" zijn (een goedkeuring kan elk moment binnenkomen,
  // en een eerder goedgekeurde sessie kan na 6 uur verlopen).
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!user) {
      setAccessStatus(null);
      return;
    }
    refreshStatus();
    // Snel pollen zolang er (nog) geen toegang is — bv. direct na een
    // Stripe-betaling, tot de webhook verwerkt is. Daarna rustig, zodat een
    // opgezegd/verlopen lidmaatschap ook binnen een minuut doorkomt.
    let last = Date.now();
    pollRef.current = setInterval(() => {
      if (statusRef.current === "approved" && Date.now() - last < 60000) return;
      last = Date.now();
      refreshStatus();
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [user, refreshStatus]);

  const doRegister = useCallback(async ({ username, password, name }) => {
    setLoading(true);
    setError("");
    try {
      await api.register({ username, password, name });
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const doLogin = useCallback(async ({ username, password }) => {
    setLoading(true);
    setError("");
    try {
      const u = await api.login({ username, password });
      setUser(u);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const doLogout = useCallback(() => {
    api.logout();
    setUser(null);
    setAccessStatus(null);
    setMembership(null);
    setDiscord(null);
  }, []);

  // Stuurt de browser naar Stripe / Discord; bij een fout tonen we die.
  const wrapRedirect = (fn) => async () => {
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    }
  };
  const startCheckout = useCallback(wrapRedirect(api.startCheckout), []); // eslint-disable-line react-hooks/exhaustive-deps
  const openBillingPortal = useCallback(wrapRedirect(api.openBillingPortal), []); // eslint-disable-line react-hooks/exhaustive-deps
  const linkDiscord = useCallback(wrapRedirect(api.linkDiscord), []); // eslint-disable-line react-hooks/exhaustive-deps
  const unlinkDiscord = useCallback(async () => {
    setError("");
    try {
      await api.unlinkDiscord();
      refreshStatus();
    } catch (e) {
      setError(e.message);
    }
  }, [refreshStatus]);

  return {
    user,
    accessStatus,
    loading,
    error,
    clearError: () => setError(""),
    register: doRegister,
    login: doLogin,
    logout: doLogout,
    membership,
    discord,
    startCheckout,
    openBillingPortal,
    linkDiscord,
    unlinkDiscord,
    refreshStatus,
  };
}
